import {
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconSearch } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { getErrors } from "api/diagnostic.service";
import { getNotification, saveNotification } from "api/notification.service";
import { formatDate } from "common/utils";
import RichTextEditor, {
  RichTextEditorRef,
} from "components/elements/editor/RichTextEditor";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLogs } from "types/model.types";

const GlobalAdmin = () => {
  const [message, setMessage] = useState<string>("");
  const editorRef = useRef<RichTextEditorRef>(null);
  const [opened, { toggle, close }] = useDisclosure(false);
  const navigate = useNavigate();
  const [selectedError, setSelectedError] = useState<string | null>(null);
  const [errorModalOpen, { open: openErrorModal, close: closeErrorModal }] =
    useDisclosure(false);
  const [logs, setLogs] = useState<AppLogs>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await safeApiCall(getNotification());
        setMessage(resp?.message ?? null);
      } catch (err) {
        setMessage(JSON.stringify(err));
      }
    })();

    (async () => {
      try {
        const result = await getErrors();
        if (result) setLogs(result);
      } catch (err) {
        setError("Failed to fetch application errors.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedSearch = (searchTerm ?? "").trim().toLowerCase();

    return logs.filter((l) => {
      const appLog = l.error?.toLowerCase() ?? "";

      const matchesSearch = [l.route, l.error].some((field) => {
        return (field ?? "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .includes(normalizedSearch);
      });

      return matchesSearch;
    });
  }, [logs, searchTerm, statusFilter]);

  const paginatedItems = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredItems, activePage]);

  const rows = paginatedItems.map((log) => {
    return (
      <Table.Tr key={log.id}>
        <Table.Td style={{ whiteSpace: "normal" }}>
          {formatDate(log.createdOn)}
        </Table.Td>
        <Table.Td>{log.route}</Table.Td>
        <Table.Td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          <Text span>{log.error?.slice(0, 100)}</Text>
          {log.error && log.error.length > 100 && (
            <Button
              size="xs"
              variant="light"
              ml="sm"
              onClick={() => {
                setSelectedError(log.error);
                openErrorModal();
              }}
            >
              View full
            </Button>
          )}
        </Table.Td>
      </Table.Tr>
    );
  });
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
  };
  const formattedError = useMemo(() => {
    if (!selectedError) return "";

    return selectedError
      .split("\n")
      .map((line, i) => {
        const isStackLine = line.trim().startsWith("at ");
        return isStackLine ? `<code>${line}</code>` : line;
      })
      .join("\n");
  }, [selectedError]);

  return (
    <Box>
      <Stack>
        {loading ? (
          <Center>
            <Loader />
          </Center>
        ) : error ? (
          <Alert color="red">{error}</Alert>
        ) : (
          <Stack>
            <Title order={2}>System Diagnostics & Notifications</Title>
            <Group justify="left" mb="md">
              <Button onClick={toggle}>Set system notification message</Button>
              <Button
                disabled={!message}
                onClick={async () => {
                  await safeApiCall(saveNotification(""));
                  navigate(0); // Redirect to the CMS page
                }}
              >
                Reset system notification message
              </Button>
            </Group>
            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(filteredItems.length / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
            <TextInput
              placeholder="Search by any title"
              mb="md"
              leftSection={<IconSearch size={16} stroke={1.5} />}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Route</Table.Th>
                  <Table.Th>Error</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Pagination
                total={Math.ceil(filteredItems.length / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
            {/* Modal popup for editing the notification message */}
            <Modal
              opened={opened}
              onClose={close}
              title="Set system notification message"
              centered
              size="xl"
            >
              <Stack
                h={300}
                bg="var(--mantine-color-body)"
                align="stretch"
                justify="center"
                gap="md"
              >
                <RichTextEditor
                  ref={editorRef}
                  content={message}
                  onChange={(html) => setMessage(html)}
                  variant={"full"}
                  height={300}
                  minHeight={300}
                  placeholder="Enter notification message here..."
                />
                <Group mt="md" align="right" justify="flex-end">
                  <Button
                    onClick={async () => {
                      try {
                        // Save the message using the API
                        await safeApiCall(saveNotification(message));
                        close();
                        navigate(0); // Redirect to the CMS page
                      } catch (err) {
                        // Handle error if needed
                      }
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      setMessage("");
                      close();
                    }}
                  >
                    Cancel
                  </Button>
                </Group>
              </Stack>
            </Modal>

            {/* Global Error Detail Modal */}
            <Modal
              opened={errorModalOpen}
              onClose={closeErrorModal}
              size="xl"
              centered
              styles={{
                content: {
                  maxHeight: "500", // prevents overflow
                  overflowY: "auto",
                },
              }}
            >
              <ScrollArea h={500} type="always">
                <Group justify="space-between" mb="sm">
                  <Text fw={800}>Full Error Message</Text>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => {
                      if (selectedError) {
                        navigator.clipboard.writeText(selectedError);
                      }
                    }}
                  >
                    Copy
                  </Button>
                </Group>

                <Text
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {selectedError}
                </Text>
              </ScrollArea>
            </Modal>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default GlobalAdmin;
